import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useIdioma } from '../i18n';
import {
  HREFLANG,
  PAGINAS,
  PREFIXOS,
  SITE_URL,
  comPrefixo,
  lerPrefixo,
  tituloCompleto
} from '../config/seo';
import { buscarPost, noIdioma, traduzirCaminho } from '../config/posts';

/** Acha ou cria a tag no `<head>` e põe o valor; `null` tira a tag. */
const definir = (tag, atributo, nome, valor) => {
  let el = document.head.querySelector(`${tag}[${atributo}="${nome}"]`);
  if (valor == null) return el?.remove();
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(atributo, nome);
    if (atributo === 'hreflang') el.setAttribute('rel', 'alternate');
    document.head.appendChild(el);
  }
  el.setAttribute(tag === 'link' ? 'href' : 'content', valor);
};

/**
 * O que a rota declara ao Google. Pura, para servir também ao pré-render
 * (`src/prerender.js`), que monta o mesmo `<head>` sem navegador.
 */
export const metadadosDe = (pathname, t, idioma) => {
  /* `/blog/` e `/blog` são a mesma página; o canonical diz qual vale. A página
     se acha sem o prefixo do idioma: `/pt-br/plans` é `/plans`. */
  const caminho = lerPrefixo(pathname).caminho.replace(/\/+$/, '') || '/';
  const slug = matchPath('/blog/:slug', caminho)?.params.slug;
  const post = slug ? buscarPost(slug) : null;
  const achada = PAGINAS.map((p) => ({ p, rota: matchPath(p.caminho, caminho) })).find(
    (x) => x.rota
  );

  const indexar = Boolean(post || achada?.p.indexar);
  const titulo = tituloCompleto(
    post
      ? noIdioma(post.seo?.titulo || post.titulo, idioma)
      : t(achada ? achada.p.titulo : 'naoEncontrada.titulo', achada?.rota.params)
  );
  const descricao = post
    ? noIdioma(post.seo?.descricao || post.resumo, idioma)
    : achada?.p.descricao
      ? t(achada.p.descricao)
      : null;
  /* A mesma página nos três idiomas, para o `hreflang`. `x-default` é a raiz,
     em inglês: é para onde vai quem não fala nenhum dos três. */
  const versao = (id) =>
    indexar ? `${SITE_URL}${comPrefixo(id, traduzirCaminho(caminho, id))}` : null;
  const alternativas = { 'x-default': versao('en') };
  for (const id of Object.keys(PREFIXOS)) alternativas[HREFLANG[id]] = versao(id);
  return {
    titulo,
    descricao,
    url: versao(idioma),
    indexar,
    tipo: post ? 'article' : 'website',
    alternativas
  };
};

/**
 * Título, description, canonical e robots da rota atual — ver `config/seo`.
 *
 * Mexe nas tags que `public/index.html` já traz em vez de renderizar
 * `<title>`/`<meta>` pelo React 19: essas entram ao lado das estáticas, e o
 * `<head>` ficaria com duas descriptions, uma delas a da home.
 */
const Metadados = () => {
  const { pathname } = useLocation();
  const { t, idioma } = useIdioma();

  useEffect(() => {
    const { titulo, descricao, url, indexar, tipo, alternativas } = metadadosDe(pathname, t, idioma);

    document.title = titulo;
    definir('meta', 'name', 'description', descricao);
    definir('meta', 'name', 'robots', indexar ? null : 'noindex, follow');
    definir('link', 'rel', 'canonical', url);
    definir('meta', 'property', 'og:title', titulo);
    definir('meta', 'property', 'og:description', descricao);
    definir('meta', 'property', 'og:url', url);
    definir('meta', 'property', 'og:type', tipo);
    for (const [codigo, href] of Object.entries(alternativas)) definir('link', 'hreflang', codigo, href);
  }, [pathname, idioma, t]);

  return null;
};

export default Metadados;
